import {useEffect, useState, useCallback} from "react";
import toast from "react-hot-toast";
import {useNavigate, useParams} from "react-router-dom";
import {fetchWithAuth} from "../libs/apiUtils";
import { ArrowLeftIcon } from '@heroicons/react/24/solid'
import {Meta} from "../types/global";
import {PencilIcon, TrashIcon, PlusIcon, ListBulletIcon} from "@heroicons/react/24/outline";
import DeleteMetadataModal from "../components/metadata/DeleteMetadataModal";
import EditMetadataModal from "../components/metadata/EditMetadataModal";
import CreateMetadataModal from "../components/metadata/CreateMetadataModal";
import Pagination from "../components/Pagination";

interface MetadataData {
  id: string
  name: string
  value: string
}

export default function Metadata() {
  const { tableId } = useParams()

  const [tableName, setTableName] = useState<string | null>(null)

  // pagination stuff
  const [loading, setLoading] = useState<boolean>(true)
  const [metadata, setMetadata] = useState<MetadataData[]>([])
  const [meta, setMeta] = useState<Meta | null>(null)
  const [page, setPage] = useState<number>(1)
  const [perPage, setPerPage] = useState<number>(10)

  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false)
  const [toBeDeletedMetadata, setToBeDeletedMetadata] = useState<{id?: string, name?: string}>({})

  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false)
  const [toBeEditedMetadata, setToBeEditedMetadata] = useState<{id?: string, name?: string, tableId?: string, value?: string}>({})

  const userRoleSlug = localStorage.getItem('user_role_slug')
  const allowedUsers = ['admin', 'super_admin']
  const doesUserHaveAccess = allowedUsers.includes(userRoleSlug ?? '')

  const navigate = useNavigate()

  function navigateBack() {
    navigate(-1)
  }

  const loadMetadata = useCallback(async (page: number = 1, tableId: string) => {
    try {
      setLoading(true)
      const res = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/api/table-metadatas`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ page, perPage, tableId }),
      })

      const data = await res.json()

      if (!res.ok || !data.success) {
        toast.error(data.message || 'Error fetching metadata data')
        return
      }

      setMetadata(data.data || [])
      setMeta(data.meta)
    } catch (err) {
      toast.error('Network error, check your connection')
    } finally {
      setLoading(false)
    }
  }, [perPage])

  async function handleDeleteMetadata(id: string) {
    try {
      const res = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/api/table-metadata/${id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      })

      const data = await res.json()

      if (!res.ok || !data.success) {
        toast.error(data[0].message || 'Error deleting metadata')
        return
      }

      toast.success('Metadata deleted successfully')
      loadMetadata(page, tableId!) 
    } catch (err) {
      toast.error('Network error, check your connection')
    }
  }

  useEffect(() => {
    if (tableId) loadMetadata(page, tableId)
  }, [page, tableId, loadMetadata])

  useEffect(() => {
    async function validateTable() {
      try {
        const res = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/api/table-detail`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tableId }),
        })

        if (!res.ok) {
          throw new Error('Invalid table')
        }

        const data = await res.json()
        setTableName(data.data.name)
      } catch (err) {
        toast.error('Table not found')
        navigate('/data/group')
      } finally {
        setLoading(false)
      }
    }

    if (tableId) validateTable()
  }, [tableId, navigate])

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-4">
          <button 
            onClick={navigateBack} 
            className="p-2 hover:bg-neutral-100 rounded-full transition-colors text-neutral-600"
            title="Kembali"
          >
            <ArrowLeftIcon className="size-6" />
          </button>
          <div>
            <nav className="flex text-sm text-neutral-500 mb-1">
              <button onClick={() => navigate('/data/group')} className="hover:text-primary-600 transition-colors">Manajemen Grup</button>
              <span className="mx-2">/</span>
              <span className="text-neutral-900 font-medium">Metadata</span>
            </nav>
            <h2 className="text-3xl font-extrabold text-neutral-900 tracking-tight">
              Tabel: <span className="text-primary-600">{tableName || '...'}</span>
            </h2>
          </div>
        </div>
        
        { doesUserHaveAccess && (
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-5 py-2.5 rounded-xl font-semibold shadow-lg shadow-primary-200 transition-all active:scale-95"
          >
            <PlusIcon className="size-5" />
            Tambah Metadata
          </button>
        )}
      </div>

      <div className="bg-white rounded-2xl shadow-xl shadow-neutral-100 border border-neutral-100 overflow-hidden">
        { loading ? (
          <div className="p-20 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-neutral-500 font-medium">Memuat data...</p>
          </div>
        ) : metadata.length === 0 ? (
          <div className="p-20 text-center">
            <div className="bg-primary-50 size-16 rounded-full flex items-center justify-center mx-auto mb-4 text-primary-600">
              {doesUserHaveAccess ? <PlusIcon className="size-8" /> : <ListBulletIcon className="size-8" />}
            </div>
            <h3 className="text-lg font-bold text-neutral-800">
              {doesUserHaveAccess ? 'Belum Ada Metadata' : 'Tidak Ada Data Metadata'}
            </h3>
            <p className="text-neutral-500 max-w-xs mx-auto mt-2">
              {doesUserHaveAccess 
                ? 'Silakan klik tombol "Tambah Metadata" untuk memulai pengelolaan data.'
                : 'Belum ada informasi metadata yang tersedia untuk tabel ini.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-neutral-50/50">
                  <th className="px-6 py-4 text-sm font-bold text-neutral-600 uppercase tracking-wider w-20">No</th>
                  <th className="px-6 py-4 text-sm font-bold text-neutral-600 uppercase tracking-wider">Nama Metadata</th>
                  <th className="px-6 py-4 text-sm font-bold text-neutral-600 uppercase tracking-wider">Nilai</th>
                  { doesUserHaveAccess &&
                    <th className="px-6 py-4 text-sm font-bold text-neutral-600 uppercase tracking-wider w-32 text-center">Aksi</th>
                  }
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {metadata.map((item, index) => (
                  <tr 
                    key={item.id} 
                    className="group hover:bg-primary-50/30 transition-colors"
                  >
                    <td className="px-6 py-4 text-sm font-medium text-neutral-500">
                      {(meta?.perPage || 20) * (page - 1) + index + 1}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-neutral-900 font-semibold">{item.name}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-neutral-700 bg-neutral-100 px-3 py-1 rounded-md inline-block">{item.value}</div>
                    </td>
                    { doesUserHaveAccess && (
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center gap-3 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            title="Edit Metadata"
                            className="p-2 text-yellow-600 hover:bg-yellow-50 rounded-lg transition-colors"
                            onClick={() => {
                              setToBeEditedMetadata({id: item.id, tableId: tableId, name: item.name, value: item.value})
                              setIsEditModalOpen(true)
                            }}
                          >
                            <PencilIcon className="size-5" />
                          </button>
                          <button
                            title="Hapus Metadata"
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            onClick={() => {
                              setToBeDeletedMetadata({id: item.id, name: item.name})
                              setIsDeleteModalOpen(true)
                            }}
                          >
                            <TrashIcon className="size-5" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {meta && (
          <div className="px-6 py-4 bg-neutral-50/50 border-t border-neutral-100">
            <Pagination
              currentPage={page}
              lastPage={meta.lastPage}
              total={meta.total}
              perPage={perPage}
              onPageChange={setPage}
              onPerPageChange={(newPerPage) => {
                setPerPage(newPerPage)
                setPage(1)
              }}
            />
          </div>
        )}
      </div>

      <CreateMetadataModal
        isOpen={isCreateModalOpen}
        setIsOpen={setIsCreateModalOpen}
        tableId={tableId!}
        onSuccess={() => loadMetadata(page, tableId!)}
      />
      <DeleteMetadataModal isOpen={isDeleteModalOpen} 
        setIsOpen={setIsDeleteModalOpen} 
        name={toBeDeletedMetadata.name ?? ''}
        onSubmit={() => handleDeleteMetadata(toBeDeletedMetadata.id ?? '')}
      />
      <EditMetadataModal 
        isOpen={isEditModalOpen}
        setIsOpen={setIsEditModalOpen}
        metadata={toBeEditedMetadata}
        onSuccess={() => loadMetadata(page, tableId!)}
      />
    </div>
  )
}
